import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@byscott-io/corebyscott';
import { dashboard, assessmentSessions } from '../api/client';

export default function Dashboard() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [pendingReviews, setPendingReviews] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const [dashboardResponse, reviewsResponse] = await Promise.all([
        dashboard.get(),
        assessmentSessions.pendingReview().catch(() => ({ data: [] })),
      ]);
      setData(dashboardResponse.data);
      setPendingReviews(reviewsResponse.data);
    } catch (err) {
      console.error('Failed to load dashboard:', err);
      setError('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="px-4 py-6 sm:px-0">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4 py-6 sm:px-0">
        <div className="text-red-500">{error}</div>
      </div>
    );
  }

  return (
    <div className="px-4 py-6 sm:px-0">
      <h1 className="text-2xl font-semibold text-gray-900">
        Welcome, {user?.first_name}!
      </h1>
      <p className="mt-2 text-gray-600">
        Track your family's educational journey and life skills development.
      </p>

      {/* Stats */}
      <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <span className="text-3xl">👨‍👩‍👧‍👦</span>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Families
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {data?.families_count || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-5 py-3">
            <Link to="/app/families" className="text-sm font-medium text-indigo-600 hover:text-indigo-500">
              View all
            </Link>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <span className="text-3xl">🎓</span>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Students
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {data?.students_count || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-5 py-3">
            <Link to="/app/families" className="text-sm font-medium text-indigo-600 hover:text-indigo-500">
              View all
            </Link>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <span className="text-3xl">📊</span>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Assessments
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {data?.assessments_count || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-5 py-3">
            <Link to="/app/families" className="text-sm font-medium text-indigo-600 hover:text-indigo-500">
              Select student
            </Link>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <span className="text-3xl">👥</span>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Groups
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {data?.groups_count || 0}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-5 py-3">
            <Link to="/app/groups" className="text-sm font-medium text-indigo-600 hover:text-indigo-500">
              View all
            </Link>
          </div>
        </div>
      </div>

      {/* Pending Reviews Alert */}
      {pendingReviews.length > 0 && (
        <div className="mt-8">
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-yellow-700">
                    <strong>{pendingReviews.length}</strong> assessment{pendingReviews.length !== 1 ? 's' : ''} waiting for review.
                    Student drawings need to be scored before results can be calculated.
                  </p>
                </div>
              </div>
              <Link
                to="/app/reviews"
                className="ml-4 inline-flex items-center px-4 py-2 bg-yellow-400 hover:bg-yellow-500 text-yellow-900 text-sm font-medium rounded-md transition-colors"
              >
                Review Now
                <svg className="ml-2 -mr-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="mt-8">
        <h2 className="text-lg font-medium text-gray-900">Quick Actions</h2>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Link
            to="/app/families"
            className="relative group bg-white p-6 rounded-lg shadow hover:shadow-md transition-shadow"
          >
            <div className="text-4xl mb-4">👨‍👩‍👧‍👦</div>
            <h3 className="text-lg font-medium text-gray-900 group-hover:text-indigo-600">
              Manage Families
            </h3>
            <p className="mt-2 text-sm text-gray-500">
              View and manage families and their students.
            </p>
          </Link>

          <Link
            to="/app/families"
            className="relative group bg-white p-6 rounded-lg shadow hover:shadow-md transition-shadow"
          >
            <div className="text-4xl mb-4">📝</div>
            <h3 className="text-lg font-medium text-gray-900 group-hover:text-indigo-600">
              Start Assessment
            </h3>
            <p className="mt-2 text-sm text-gray-500">
              Select a student to begin an assessment.
            </p>
          </Link>

          <Link
            to="/app/groups"
            className="relative group bg-white p-6 rounded-lg shadow hover:shadow-md transition-shadow"
          >
            <div className="text-4xl mb-4">👥</div>
            <h3 className="text-lg font-medium text-gray-900 group-hover:text-indigo-600">
              Manage Groups
            </h3>
            <p className="mt-2 text-sm text-gray-500">
              Organize students into co-ops and classes.
            </p>
          </Link>

          <Link
            to="/app/reviews"
            className="relative group bg-white p-6 rounded-lg shadow hover:shadow-md transition-shadow"
          >
            <div className="relative text-4xl mb-4">
              ✏️
              {pendingReviews.length > 0 && (
                <span className="absolute -top-1 -right-1 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-red-600 rounded-full">
                  {pendingReviews.length}
                </span>
              )}
            </div>
            <h3 className="text-lg font-medium text-gray-900 group-hover:text-indigo-600">
              Review Drawings
            </h3>
            <p className="mt-2 text-sm text-gray-500">
              Score student drawings and responses.
            </p>
          </Link>
        </div>
      </div>

      {/* Recent Students */}
      {data?.recent_students?.length > 0 && (
        <div className="mt-8">
          <h2 className="text-lg font-medium text-gray-900">Recent Students</h2>
          <div className="mt-4 bg-white shadow overflow-hidden rounded-lg">
            <ul className="divide-y divide-gray-200">
              {data.recent_students.map((student) => (
                <li key={student.id}>
                  <Link
                    to={`/app/students/${student.id}`}
                    className="block hover:bg-gray-50"
                  >
                    <div className="px-4 py-4 sm:px-6">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-indigo-600 truncate">
                          {student.full_name}
                        </p>
                        <div className="ml-2 flex-shrink-0 flex">
                          {student.grade_level && (
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                              Grade {student.grade_level}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="mt-2 sm:flex sm:justify-between">
                        <div className="sm:flex">
                          <p className="flex items-center text-sm text-gray-500">
                            {student.family_name}
                          </p>
                        </div>
                        {student.age && (
                          <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0">
                            {student.age} years old
                          </div>
                        )}
                      </div>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
